import * as React from 'react';
import styled from 'styled-components';
import Headline from '@adecco/base-app/src/components/atoms/Headline/Headline';
import Paragraph from '@adecco/base-app/src/components/atoms/Paragraph/Paragraph';
import calcrem from '@adecco/base-app/src/utilities/calcrem/calcrem';
import { moreThan } from '@adecco/base-app/src/constants/breakpoints/breakpoints';
import { CloudinaryImageType } from '@adecco/base-app/src/gql/schema/generated';
import DynamicImage from '@adecco/base-app/src/components/atoms/DynamicImage/DynamicImage';

export interface IProcessItem {
  title: string;
  description: string;
  image?: string;
  number?: number;
  cloudinaryImage?: CloudinaryImageType;
}

const ProcessItemContainer = styled.div`
  border: ${(props) => props.theme.borders.materialDown};
  border-radius: ${(props) => props.theme.radius.panel};
  height: 100%;

  ${moreThan('md')} {
    border: 0;
    border-radius: 0;
  }
`;

const ProcessItemImage = styled.div`
  overflow: hidden;
  padding-bottom: 100%;
  position: relative;
  background-color: ${(props) => props.theme.colors.lightGrey};
  border-radius: ${(props) => props.theme.radius.processItemImage};
  box-shadow: ${(props) => props.theme.boxShadow.processItemImage};

  ${moreThan('md')} {
    border: 0;
    border-radius: ${(props) => props.theme.radius.panel};
  }
`;

const Image = styled(DynamicImage)`
  position: absolute;
  aspect-ratio: 1;
  object-fit: contain;
  top: 0;
  left: 0;
  width: 100%;
`;

const NumberBackground = styled.div`
  position: absolute;
  width: ${(props) => props.theme.widths.processNumber};
  height: 35px;
  border: ${(props) => props.theme.brandColor && `2px solid ${props.theme.brandColor}`};
  border-radius: ${(props) => props.theme.radius.processNumber};
  background-color: ${(props) =>
    props.theme.brandColor ? 'white' : props.theme.backgrounds.processNumber};
  bottom: ${(props) => props.theme.positions.processNumberBottom};
  left: ${(props) => props.theme.positions.processNumberLeft};
  mix-blend-mode: ${(props) => props.theme.blendModes.processNumber};

  ${moreThan('md')} {
    bottom: ${(props) => props.theme.positions.processNumberBottomWideScreen};
    left: ${(props) => props.theme.positions.processNumberLeftWideScreen};
  }
`;

const Number = styled.div`
  width: ${(props) => props.theme.widths.processNumber};
  height: 35px;
  font-family: ${(props) => props.theme.fonts.Copy};
  font-weight: ${(props) => props.theme.fontWeights.processItemNumber};
  font-size: ${(props) => props.theme.fontSizes.processItemNumber};
  line-height: ${(props) => props.theme.lineHeights.processItemNumber};
  color: ${(props) => (props.theme.brandColor ? `#333` : props.theme.colors.processNumber)};
  position: absolute;
  bottom: ${(props) => props.theme.positions.processNumberBottom};
  left: ${(props) => props.theme.positions.processNumberLeft};
  display: ${(props) => props.theme.displays.processNumber};
  justify-content: center;
  align-items: center;

  ${moreThan('md')} {
    bottom: ${(props) => props.theme.positions.processNumberBottomWideScreen};
    left: ${(props) => props.theme.positions.processNumberLeftWideScreen};
  }
`;

const ProcessItemCopyContainer = styled.div`
  padding: ${(props) => props.theme.paddings.processItemContainer};
  min-height: calc(${calcrem(12)} + ${calcrem(30)} + ${calcrem(120)});

  ${moreThan('md')} {
    padding: ${(props) => props.theme.paddings.processItemContainerWideScreen};
    min-height: auto;
  }
`;

const ProcessItemHeadline = styled(Headline)`
  font-family: ${(props) => props.theme.fonts.HeadlineBold};
  font-size: ${(props) => props.theme.fontSizes.headline4};
  line-height: ${(props) => props.theme.lineHeights.processItemHeadline};
  margin-bottom: ${(props) => props.theme.margins.processItemHeadline};
  color: ${(props) => props.theme.colors.processItemHeadline};
`;

const ProcessItemParagraph = styled(Paragraph)`
  line-height: ${(props) => props.theme.lineHeights.paragraph};
  color: ${(props) => props.theme.colors.processItemParagraph};
  font-size: ${(props) => props.theme.fontSizes.processItemParagraph};
`;

const ProcessItem: React.FunctionComponent<IProcessItem> = ({
  title,
  description,
  image,
  number,
  cloudinaryImage,
}) => (
  <ProcessItemContainer>
    <ProcessItemImage>
      <Image image={image} cloudinaryImage={cloudinaryImage} alt={title} />
      {number && (
        <>
          <NumberBackground />
          <Number>{number}</Number>
        </>
      )}
    </ProcessItemImage>
    <ProcessItemCopyContainer>
      <ProcessItemHeadline skin={3} tag="h4">
        {title}
      </ProcessItemHeadline>
      <ProcessItemParagraph>{description}</ProcessItemParagraph>
    </ProcessItemCopyContainer>
  </ProcessItemContainer>
);

export default ProcessItem;
